import type { LmsAdapter } from './types';

const LOG_PREFIX = '[lms-language]';

let cachedLanguage = '';
let inflight: Promise<string> | null = null;

/** Filter browser console with `[lms-language]` to trace locale auto-detect. */
export function logLmsLanguage(
  step: string,
  detail?: Record<string, unknown>
): void {
  if (detail !== undefined) {
    console.info(LOG_PREFIX, step, detail);
  } else {
    console.info(LOG_PREFIX, step);
  }
}

/**
 * Single-flight read of `cmi.*_preference.language` (onboarding language picker).
 */
export async function fetchLearnerLanguagePreference(
  lms: LmsAdapter
): Promise<string> {
  if (cachedLanguage) {
    logLmsLanguage('cache hit', { value: cachedLanguage });
    return cachedLanguage;
  }
  if (inflight) {
    logLmsLanguage('await in-flight request');
    return inflight;
  }

  logLmsLanguage('fetch start', {
    adapter: lms.name,
    isAvailable: lms.isAvailable
  });

  inflight = (async () => {
    let value = '';
    try {
      value = ((await lms.getLearnerLanguagePreference()) || '').trim();
    } catch (error) {
      logLmsLanguage('adapter read failed', {
        adapter: lms.name,
        error: error instanceof Error ? error.message : String(error)
      });
      value = '';
    }
    if (value) {
      cachedLanguage = value;
    }
    logLmsLanguage('fetch done', {
      adapter: lms.name,
      value: value || '(empty)',
      cached: Boolean(value)
    });
    inflight = null;
    return value;
  })();

  return inflight;
}

export function peekLearnerLanguagePreference(): string {
  return cachedLanguage;
}

/** Test-only reset. */
export function _resetLearnerLanguagePreferenceForTests(): void {
  cachedLanguage = '';
  inflight = null;
}
